<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">常见问题</h1>
            <p class="list-page-description">面向全部用户的问答知识库，支持分类、版本与评价管理。</p>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <button
              v-permission="'kb:faq:edit'"
              @click="openFormModal()"
              class="btn-primary inline-flex items-center gap-2"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              新增FAQ
            </button>
          </div>
        </div>
      </div>
    </template>
    <div class="min-h-full overflow-x-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
    <div class="relative overflow-hidden">
      <div class="pointer-events-none absolute -right-40 -top-24 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl"></div>
      <div class="pointer-events-none absolute left-10 top-36 h-40 w-40 rounded-full bg-slate-200/60 blur-3xl"></div>

      <div class="mx-auto w-full px-4 py-6">

        <!-- 主体区域 -->
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <!-- 左侧分类树 -->
          <div class="lg:col-span-3">
            <KbCategoryTree
              :tree="categoryTree"
              :loading="categoryLoading"
              :selected-id="selectedCategoryId"
              
              title="FAQ分类"
              description="支持多级分类，拖拽排序。"
              @add="handleAddCategory"
              @add-child="handleAddChildCategory"
              @edit="handleEditCategory"
              @delete="confirmDeleteCategory"
              @select="handleCategoryTreeSelect"
            />
          </div>

          <!-- 右侧FAQ列表 -->
          <div class="lg:col-span-9">
            <div class="list-page-table-panel">
              <!-- 搜索栏 -->
              <div class="border-b border-slate-200 p-5">
                <div>
                  <h2 class="text-base font-semibold text-slate-900">FAQ列表</h2>
                  <p class="mt-1 text-xs text-slate-500">编辑将自动创建新版本，可随时回滚。</p>
                </div>
                <PageFilterBar
                  shell-class="filter-bar-shell is-embedded"
                  body-class="filter-bar-body is-embedded"
                  content-class="flex flex-col gap-3"
                  :show-refresh="true"
                  :refresh-loading="listLoading"
                  @refresh="loadFaqList(pageInfo.current)"
                >
                  <div class="grid grid-cols-1 gap-3 lg:grid-cols-4">
                    <div class="relative lg:col-span-2">
                      <input
                        v-model="searchKeyword"
                        type="text"
                        placeholder="搜索问题或关键词"
                        class="form-search-input form-control-sm w-full"
                        @keyup.enter="loadFaqList(1)"
                      >
                      <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <select v-model="searchStatus" class="form-select-native form-control-sm">
                      <option value="">全部状态</option>
                      <option value="0">草稿</option>
                      <option value="1">已发布</option>
                      <option value="2">已归档</option>
                    </select>
                  </div>

                  <template #searchActions>
                    <button
                      @click="loadFaqList(1)"
                      class="btn-primary"
                    >查询</button>
                    <button
                      @click="resetFilters"
                      class="btn-secondary"
                    >重置</button>
                  </template>
                </PageFilterBar>
              </div>

              <!-- 表格 -->
              <div class="data-table-shell">
                <div class="data-table-scroll">
                  <table class="data-table text-sm">
                    <thead class="data-table-head">
                    <tr>
                      <th class="px-5 py-3 text-left">问题</th>
                      <th class="px-5 py-3 text-left">标签</th>
                      <th class="px-5 py-3 text-left">分类</th>
                      <th class="px-5 py-3 text-center">状态</th>
                      <th class="px-5 py-3 text-center">置顶</th>
                      <th class="px-5 py-3 text-center">浏览</th>
                      <th class="px-5 py-3 text-center">评价</th>
                      <th class="px-5 py-3 text-center">版本</th>
                      <th class="px-5 py-3 text-center">操作</th>
                    </tr>
                  </thead>
                    <tbody>
                    <template v-if="listLoading">
                      <tr v-for="n in 5" :key="n" class="data-table-row">
                        <td class="data-table-td"><div class="data-table-skeleton h-4 w-48"></div></td>
                        <td class="data-table-td"><div class="data-table-skeleton h-4 w-20"></div></td>
                        <td class="data-table-td"><div class="data-table-skeleton h-4 w-16"></div></td>
                        <td class="data-table-td text-center"><div class="data-table-skeleton mx-auto h-5 w-14 rounded-full"></div></td>
                        <td class="data-table-td text-center"><div class="data-table-skeleton mx-auto h-4 w-8"></div></td>
                        <td class="data-table-td text-center"><div class="data-table-skeleton mx-auto h-4 w-10"></div></td>
                        <td class="data-table-td text-center"><div class="data-table-skeleton mx-auto h-4 w-16"></div></td>
                        <td class="data-table-td text-center"><div class="data-table-skeleton mx-auto h-4 w-8"></div></td>
                        <td class="data-table-td"><div class="mx-auto flex justify-center gap-2"><div class="data-table-skeleton h-7 w-7 rounded-full"></div><div class="data-table-skeleton h-7 w-7 rounded-full"></div><div class="data-table-skeleton h-7 w-7 rounded-full"></div></div></td>
                      </tr>
                    </template>
                    <tr v-else-if="!faqList.length">
                      <td colspan="9" class="px-5 py-8 text-center text-sm text-slate-500">暂无FAQ数据</td>
                    </tr>
                    <tr v-for="item in faqList" :key="item.id" class="data-table-row group/row">
                      <td class="data-table-td">
                        <div class="flex items-start gap-2.5 max-w-[260px]">
                          <div class="flex-shrink-0 mt-0.5">
                            <div class="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-primary-50 to-primary-100 text-primary-600">
                              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                          <div class="min-w-0 flex-1">
                            <div
                              class="data-table-cell-title cursor-default line-clamp-2 group-hover/row:text-primary-600 transition-colors"
                              :title="item.title"
                            >{{ item.title }}</div>
                            <div class="data-table-cell-meta mt-1 flex items-center gap-2">
                              <span>{{ item.createTime?.slice(0, 10) || '-' }}</span>
                              <span v-if="item.createByName">· {{ item.createByName }}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="data-table-td">
                        <div v-if="item.tags" class="flex flex-wrap gap-1 max-w-[150px]">
                          <span
                            v-for="tag in item.tags.split(',')"
                            :key="tag"
                            class="data-table-inline-tag"
                          >{{ tag.trim() }}</span>
                        </div>
                        <span v-else class="text-slate-300">-</span>
                      </td>
                      <td class="data-table-td text-slate-600">{{ getLastCategoryName(item.categoryId) }}</td>
                      <td class="data-table-td text-center">
                        <span :class="statusClass(item.status)">{{ statusText(item.status) }}</span>
                      </td>
                      <td class="data-table-td text-center">
                        <span v-if="item.isTop" class="text-amber-500">
                          <svg class="mx-auto h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </span>
                        <span v-else class="text-slate-300">-</span>
                      </td>
                      <td class="data-table-td text-center text-slate-600">{{ item.viewCount || 0 }}</td>
                      <td class="data-table-td text-center">
                        <div class="flex items-center justify-center gap-2 text-xs">
                          <span class="text-emerald-600">{{ item.likeCount || 0 }}</span>
                          <span class="text-slate-300">/</span>
                          <span class="text-red-500">{{ item.dislikeCount || 0 }}</span>
                        </div>
                      </td>
                      <td class="data-table-td text-center">
                        <button
                          @click="openVersionDrawer(item)"
                          class="text-xs text-primary-600 hover:text-primary-700 hover:underline"
                        >v{{ item.currentVersion || 1 }}</button>
                      </td>
                      <td class="data-table-td">
                        <div class="flex items-center justify-center gap-1">
                          <button
                            @click="openDetailModal(item)"
                            class="table-action-button is-primary"
                            title="查看详情"
                          >
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </button>
                          <button
                            v-permission="'kb:faq:edit'"
                            @click="openFormModal(item)"
                            class="table-action-button is-primary"
                            title="编辑"
                          >
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </button>
                          <!-- 更多操作 -->
                          <div class="relative more-menu-container">
                            <button
                              v-permission="'kb:faq:edit'"
                              :ref="el => setMoreBtnRef(item.id, el)"
                              @click.stop="toggleMoreMenu(item.id, $event)"
                              class="table-action-button is-neutral"
                              title="更多操作"
                            >
                              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- 分页 -->
              <AppPagination
                v-if="pageInfo.total > 0"
                class="pagination-bridge"
                :pagination="pageInfo"
                :size="pageSize"
                :page-sizes="[10, 20, 50]"
                @page-change="handlePageChange"
                @size-change="handlePageSizeChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分类新增/编辑弹窗 -->
    <Transition name="modal" @after-leave="onModalLeave">
      <div v-if="showCategoryModal" class="fixed inset-0 z-50" @keydown.esc="closeCategoryModal">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" @click="closeCategoryModal"></div>
        <div class="relative mx-auto flex min-h-screen w-full max-w-md items-center px-4 py-8">
          <div class="w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 transition-all duration-300 ease-out" :class="reducedMotion ? '' : 'translate-y-0 opacity-100'">
            <!-- 弹窗头部 -->
            <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
              <div class="absolute inset-0 bg-grid-white/10"></div>
              <div class="relative flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-white">{{ isCategoryEdit ? '编辑分类' : '新增分类' }}</h3>
                  <p class="mt-0.5 text-sm text-primary-100">{{ isCategoryEdit ? '修改分类信息' : '创建新的FAQ分类' }}</p>
                </div>
                <button @click="closeCategoryModal" class="cursor-pointer rounded-full p-1.5 text-white/80 transition-all hover:bg-white/20 hover:text-white">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <!-- 基础信息区块 -->
            <div class="space-y-5 px-6 py-6">
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  分类名称 <span class="text-red-500">*</span>
                </label>
                <input v-model="categoryForm.name" class="form-input" placeholder="请输入分类名称">
              </div>
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  上级分类
                </label>
                <select v-model="categoryForm.parentId" class="form-select-native">
                  <option :value="0">顶级分类</option>
                  <option v-for="cat in flatCategories" :key="cat.id" :value="cat.id">{{ cat.label }}</option>
                </select>
              </div>
            </div>

            <!-- 分隔线 -->
            <div class="mx-6 border-t border-dashed border-slate-200"></div>

            <!-- 可选信息区块 -->
            <div class="space-y-5 px-6 py-6">
              <p class="text-xs font-medium text-slate-400 uppercase tracking-wider">可选信息</p>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                    排序号
                  </label>
                  <input v-model.number="categoryForm.sort" type="number" class="form-input" placeholder="0">
                </div>
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    状态
                  </label>
                  <select v-model.number="categoryForm.status" class="form-select-native">
                    <option :value="0">启用</option>
                    <option :value="1">停用</option>
                  </select>
                </div>
              </div>
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                  备注
                </label>
                <textarea v-model="categoryForm.remark" rows="2" class="form-textarea" placeholder="可选备注信息"></textarea>
                <p class="form-helper">填写备注有助于分类管理</p>
              </div>
            </div>

            <!-- 底部按钮 -->
            <div class="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button @click="closeCategoryModal" class="btn-secondary">取消</button>
              <button @click="submitCategory" class="btn-primary hover:shadow-md hover:shadow-primary-500/30">
                {{ isCategoryEdit ? '保存修改' : '创建分类' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- FAQ新增/编辑弹窗 -->
    <div v-if="showFormModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeFormModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-3xl w-full max-h-[90vh] overflow-hidden">
          <!-- 弹窗头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-white">{{ isEdit ? '编辑FAQ' : '新增FAQ' }}</h3>
                <p class="mt-0.5 text-sm text-primary-100">{{ isEdit ? '编辑将自动产生新版本记录' : '创建新的常见问题' }}</p>
              </div>
              <button @click="closeFormModal" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 表单内容 -->
          <div class="overflow-y-auto max-h-[calc(90vh-180px)] px-6 py-6 space-y-5">
            <!-- 基本信息区块 -->
            <div class="space-y-4">
              <div class="flex items-center gap-2 text-sm font-medium text-slate-900">
                <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                基本信息
              </div>
              <div class="space-y-4 rounded-xl border border-slate-200 bg-slate-50/30 p-4">
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                    所属分类 <span class="text-red-500">*</span>
                  </label>
                  <select v-model.number="faqForm.categoryId" class="form-select-native">
                    <option value="">请选择分类</option>
                    <option v-for="cat in flatCategories" :key="cat.id" :value="cat.id">{{ cat.label }}</option>
                  </select>
                </div>
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    标题 <span class="text-red-500">*</span>
                  </label>
                  <input v-model="faqForm.title" class="form-input" placeholder="请输入FAQ标题">
                </div>
              </div>
            </div>
            <!-- 内容区块 -->
            <div class="space-y-4">
              <div class="flex items-center gap-2 text-sm font-medium text-slate-900">
                <div class="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                </div>
                内容 <span class="text-red-500">*</span>
              </div>
              <RichTextEditor v-model="faqForm.content" upload-module="faq" />
            </div>
            <!-- 标签和设置区块 -->
            <div class="space-y-4">
              <div class="flex items-center gap-2 text-sm font-medium text-slate-900">
                <div class="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                </div>
                标签与设置
              </div>
              <div class="space-y-4 rounded-xl border border-slate-200 bg-slate-50/30 p-4">
                <div class="space-y-1.5">
                  <label class="text-sm font-medium text-slate-700">标签</label>
                  <input v-model="faqForm.tags" class="form-input" placeholder="多个标签用逗号分隔，如：加盟,费用,政策">
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div class="space-y-1.5">
                    <label class="text-sm font-medium text-slate-700">状态</label>
                    <select v-model.number="faqForm.status" class="form-select-native">
                      <option :value="0">草稿</option>
                      <option :value="1">已发布</option>
                    </select>
                  </div>
                  <div class="space-y-1.5">
                    <label class="text-sm font-medium text-slate-700">置顶</label>
                    <select v-model.number="faqForm.isTop" class="form-select-native">
                      <option :value="0">否</option>
                      <option :value="1">是</option>
                    </select>
                  </div>
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium text-slate-700">备注</label>
                  <textarea v-model="faqForm.remark" rows="2" class="form-textarea" placeholder="可选备注信息"></textarea>
                </div>
              </div>
            </div>
          </div>
          <!-- 底部按钮 -->
          <div class="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            <button @click="closeFormModal" class="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900">取消</button>
            <button @click="submitFaq" class="cursor-pointer rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-primary-600/30 transition-all hover:bg-primary-700 hover:shadow-md hover:shadow-primary-600/40">保存</button>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ详情弹窗 -->
    <div v-if="showDetailModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showDetailModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-3xl w-full max-h-[90vh] overflow-hidden">
          <!-- 弹窗头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white">FAQ详情</h3>
                  <p class="mt-0.5 text-sm text-primary-100">查看问答详细内容</p>
                </div>
              </div>
              <button @click="showDetailModal = false" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 内容区域 -->
          <div class="overflow-y-auto max-h-[calc(90vh-100px)] px-6 py-6">
            <div v-if="detailLoading" class="space-y-4 animate-pulse">
              <div class="h-8 w-3/4 rounded-lg bg-slate-200"></div>
              <div class="h-5 w-1/2 rounded bg-slate-200"></div>
              <div class="h-40 rounded-xl bg-slate-200"></div>
            </div>
            <template v-else-if="detailData">
              <!-- 状态和元信息 -->
              <div class="flex flex-wrap items-center gap-3">
                <span :class="statusClass(detailData.status)">{{ statusText(detailData.status) }}</span>
                <span v-if="detailData.isTop" class="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                  <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  置顶
                </span>
                <div class="flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  v{{ detailData.currentVersion }}
                </div>
              </div>
              <!-- 标题 -->
              <h2 class="mt-4 text-xl font-bold text-slate-900">{{ detailData.title }}</h2>
              <!-- 标签 -->
              <div v-if="detailData.tags" class="mt-3 flex flex-wrap gap-1.5">
                <span
                  v-for="tag in (detailData.tags || '').split(',').filter(Boolean)"
                  :key="tag"
                  class="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600"
                >{{ tag.trim() }}</span>
              </div>
              <!-- 统计信息 -->
              <div class="mt-4 flex items-center gap-4 text-xs text-slate-500">
                <div class="flex items-center gap-1.5">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  浏览 {{ detailData.viewCount || 0 }} 次
                </div>
                <div class="flex items-center gap-1.5 text-emerald-600">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>
                  有用 {{ detailData.likeCount || 0 }}
                </div>
                <div class="flex items-center gap-1.5 text-red-500">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" /></svg>
                  无用 {{ detailData.dislikeCount || 0 }}
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  {{ detailData.categoryName || getCategoryName(detailData.categoryId) }}
                </div>
              </div>
              <!-- 内容 -->
              <div class="mt-6">
                <div class="flex items-center gap-2 text-sm font-medium text-slate-900 mb-3">
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                  </div>
                  答案内容
                </div>
                <div class="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
                  <div class="prose prose-sm max-w-none text-slate-700" v-html="detailData.content"></div>
                </div>
              </div>
              <!-- 元信息 -->
              <div class="mt-6 flex flex-wrap items-center gap-4 rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-3 text-xs text-slate-500">
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  创建：{{ detailData.createTime || '-' }}
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  更新：{{ detailData.updateTime || '-' }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 版本历史抽屉 -->
    <div v-if="showVersionDrawer" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showVersionDrawer = false"></div>
      <div class="absolute right-0 top-0 h-full w-full max-w-lg overflow-hidden bg-white shadow-2xl ring-1 ring-black/5">
        <div class="flex h-full flex-col">
          <!-- 抽屉头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div class="min-w-0 flex-1 mr-4">
                <div class="flex items-center gap-2">
                  <svg class="h-5 w-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <h3 class="text-lg font-semibold text-white">版本历史</h3>
                </div>
                <p class="mt-1 text-sm text-primary-100 truncate">{{ versionFaqItem?.title }}</p>
              </div>
              <button @click="showVersionDrawer = false" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 版本列表 -->
          <div class="flex-1 overflow-y-auto px-6 py-5">
            <div v-if="versionLoading" class="space-y-4 animate-pulse">
              <div v-for="n in 3" :key="n" class="rounded-xl border border-slate-200 p-4">
                <div class="h-5 w-16 rounded-full bg-slate-200"></div>
                <div class="mt-3 h-4 w-40 rounded bg-slate-200"></div>
                <div class="mt-2 h-3 w-24 rounded bg-slate-200"></div>
              </div>
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="version in versionList"
                :key="version.id"
                class="group cursor-pointer rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-primary-200 hover:shadow-md"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">v{{ version.versionNumber }}</span>
                    <span v-if="version.versionNumber === versionFaqItem?.currentVersion" class="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                      <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                      当前版本
                    </span>
                  </div>
                  <button
                    v-if="version.versionNumber !== versionFaqItem?.currentVersion"
                    v-permission="'kb:faq:edit'"
                    @click="handleRollback(version)"
                    class="cursor-pointer rounded-lg px-2.5 py-1 text-xs font-medium text-primary-600 opacity-0 transition-all hover:bg-primary-50 group-hover:opacity-100"
                  >回滚</button>
                </div>
                <div class="mt-3 text-sm font-medium text-slate-900">{{ version.title }}</div>
                <div v-if="version.changeSummary" class="mt-1.5 text-xs text-slate-500 line-clamp-2">{{ version.changeSummary }}</div>
                <div class="mt-3 flex items-center gap-3 text-xs text-slate-400">
                  <span class="flex items-center gap-1">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    {{ version.createTime }}
                  </span>
                  <span class="flex items-center gap-1">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    {{ version.createByName || '系统' }}
                  </span>
                </div>
              </div>
              <!-- 空状态 -->
              <div v-if="!versionList.length" class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-12">
                <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <p class="mt-3 text-sm text-slate-500">暂无版本记录</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 确认删除弹窗 -->
    <div v-if="showConfirmModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showConfirmModal = false"></div>
      <div class="relative mx-auto flex min-h-screen max-w-sm items-center px-4 py-8">
        <div class="w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          <!-- 图标区域 -->
          <div class="flex flex-col items-center px-6 pt-8 pb-4">
            <div class="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
              <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 class="mt-4 text-lg font-semibold text-slate-900">确认删除</h3>
            <p class="mt-2 text-center text-sm text-slate-600 leading-relaxed">{{ confirmMessage }}</p>
          </div>
          <!-- 底部按钮 -->
          <div class="flex gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            <button @click="showConfirmModal = false" class="flex-1 cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900">取消</button>
            <button @click="confirmDeleteAction" class="flex-1 cursor-pointer rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm shadow-red-600/30 transition-all hover:bg-red-700 hover:shadow-md hover:shadow-red-600/40">删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 更多操作下拉菜单 (Teleport到body避免overflow问题) -->
    <Teleport to="body">
      <div
        v-if="openMoreId !== null"
        class="fixed z-[9999] w-40 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl ring-1 ring-black/5"
        :style="moreMenuStyle"
        @click.stop
      >
        <button
          v-if="openMoreItem?.status === 0"
          v-permission="'kb:faq:edit'"
          @click="handlePublish(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          发布
        </button>
        <button
          v-if="openMoreItem?.status === 1"
          v-permission="'kb:faq:edit'"
          @click="handleArchive(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
          归档
        </button>
        <button
          v-permission="'kb:faq:edit'"
          @click="handleToggleTop(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
          {{ openMoreItem?.isTop ? '取消置顶' : '置顶' }}
        </button>
        <div class="my-1 border-t border-slate-100"></div>
        <button
          v-permission="'kb:faq:edit'"
          @click="confirmDeleteFaq(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-red-600 transition-colors hover:bg-red-50"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          删除
        </button>
      </div>
    </Teleport>
    </div>
  </ListPageShell>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import KbCategoryTree from './components/KbCategoryTree.vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'
import { useKbCategoryManagement } from './composables/useKbCategoryManagement'
import {
  getFaqCategoryTree, createFaqCategory, updateFaqCategory, deleteFaqCategory,
  getFaqItemPage, getFaqItemDetail, createFaqItem, updateFaqItem, deleteFaqItem,
  publishFaqItem, archiveFaqItem, toggleFaqItemTop,
  getFaqVersions, rollbackFaqItem
} from '@/api/kb'

// ==================== 分类相关 ====================
const categoryState = useKbCategoryManagement({
  loadCategoryTree: getFaqCategoryTree,
  createCategory: createFaqCategory,
  updateCategory: updateFaqCategory,
  defaultStatus: 1,
  includeRemark: true,
  messages: {
    loadError: '\u52a0\u8f7d\u5206\u7c7b\u5931\u8d25',
    emptyNameWarning: '\u8bf7\u8f93\u5165\u5206\u7c7b\u540d\u79f0',
    updateSuccess: '\u5206\u7c7b\u66f4\u65b0\u6210\u529f',
    createSuccess: '\u5206\u7c7b\u521b\u5efa\u6210\u529f',
    saveError: '\u4fdd\u5b58\u5206\u7c7b\u5931\u8d25'
  }
})
const categoryTree = categoryState.categoryTree
const categoryLoading = categoryState.categoryLoading
const selectedCategoryId = categoryState.selectedCategoryId
const showCategoryModal = categoryState.showCategoryModal
const isCategoryEdit = categoryState.isCategoryEdit
const categoryForm = categoryState.categoryForm
const loadCategories = categoryState.loadCategories
const flatCategories = categoryState.flatCategories
const getCategoryName = categoryState.getCategoryName
const getLastCategoryName = categoryState.getLastCategoryName
const handleCategorySelect = categoryState.handleCategorySelect
const handleAddCategory = categoryState.handleAddCategory
const handleAddChildCategory = categoryState.handleAddChildCategory
const handleEditCategory = categoryState.handleEditCategory
const closeCategoryModal = categoryState.closeCategoryModal
const submitCategory = categoryState.submitCategory

const faqList = ref([])
const listLoading = ref(false)
const searchKeyword = ref('')
const searchStatus = ref('')
const pageSize = ref(20)
const pageInfo = reactive({ current: 1, total: 0, hasPrevious: false, hasNext: false })

const loadFaqList = async (page = 1) => {
  listLoading.value = true
  try {
    const params = {
      page,
      size: pageSize.value,
      categoryId: selectedCategoryId.value || undefined,
      keyword: searchKeyword.value || undefined,
      status: searchStatus.value === '' ? undefined : Number(searchStatus.value)
    }
    const data = await getFaqItemPage(params)
    faqList.value = data.records || []
    Object.assign(pageInfo, {
      current: data.current || 1,
      total: data.total || 0,
      hasPrevious: data.hasPrevious,
      hasNext: data.hasNext
    })
  } catch (error) {
    ElMessage.error(error.message || '加载FAQ列表失败')
  } finally {
    listLoading.value = false
  }
}

const resetFilters = () => {
  searchKeyword.value = ''
  searchStatus.value = ''
  selectedCategoryId.value = null
  loadFaqList(1)
}

const handleCategoryTreeSelect = (id) => {
  handleCategorySelect(id, () => loadFaqList(1))
}

const handlePageChange = (page) => {
  loadFaqList(page)
}

const handlePageSizeChange = (size) => {
  pageSize.value = size
  loadFaqList(1)
}

const statusText = (status) => ({ 0: '草稿', 1: '已发布', 2: '已归档' }[status] || '未知')
const statusClass = (status) => ({
  0: 'status-badge bg-slate-100 text-slate-600',
  1: 'status-badge bg-emerald-100 text-emerald-700',
  2: 'status-badge bg-amber-100 text-amber-700'
}[status] || 'status-badge bg-slate-100 text-slate-600')

// ==================== FAQ表单弹窗 ====================
const showFormModal = ref(false)
const isEdit = ref(false)
const faqForm = reactive({
  id: null, categoryId: '', title: '', content: '', tags: '', status: 0, isTop: 0, remark: ''
})

const openFormModal = async (item) => {
  if (item) {
    isEdit.value = true
    try {
      const data = await getFaqItemDetail(item.id)
      Object.assign(faqForm, {
        id: data.id,
        categoryId: data.categoryId,
        title: data.title,
        content: data.content || '',
        tags: data.tags || '',
        status: data.status,
        isTop: data.isTop || 0,
        remark: data.remark || ''
      })
    } catch (error) {
      ElMessage.error(error.message || '加载FAQ详情失败')
      return
    }
  } else {
    isEdit.value = false
    Object.assign(faqForm, {
      id: null,
      categoryId: selectedCategoryId.value || '',
      title: '', content: '', tags: '', status: 0, isTop: 0, remark: ''
    })
  }
  showFormModal.value = true
}

const closeFormModal = () => { showFormModal.value = false }

const submitFaq = async () => {
  if (!faqForm.categoryId) { ElMessage.warning('请选择分类'); return }
  if (!faqForm.title.trim()) { ElMessage.warning('请输入标题'); return }
  if (!faqForm.content || faqForm.content === '<p></p>') { ElMessage.warning('请输入内容'); return }

  try {
    if (isEdit.value) {
      await updateFaqItem(faqForm.id, {
        categoryId: faqForm.categoryId,
        title: faqForm.title,
        content: faqForm.content,
        tags: faqForm.tags,
        status: faqForm.status,
        isTop: faqForm.isTop,
        remark: faqForm.remark
      })
      ElMessage.success('FAQ更新成功')
    } else {
      await createFaqItem({
        categoryId: faqForm.categoryId,
        title: faqForm.title,
        content: faqForm.content,
        tags: faqForm.tags,
        status: faqForm.status,
        isTop: faqForm.isTop,
        remark: faqForm.remark
      })
      ElMessage.success('FAQ创建成功')
    }
    closeFormModal()
    await loadFaqList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '保存FAQ失败')
  }
}

// ==================== FAQ详情弹窗 ====================
const showDetailModal = ref(false)
const detailData = ref(null)
const detailLoading = ref(false)

const openDetailModal = async (item) => {
  showDetailModal.value = true
  detailLoading.value = true
  try {
    detailData.value = await getFaqItemDetail(item.id)
  } catch (error) {
    ElMessage.error(error.message || '加载详情失败')
  } finally {
    detailLoading.value = false
  }
}

// ==================== 操作 ====================
const handlePublish = async (item) => {
  openMoreId.value = null
  try {
    await publishFaqItem(item.id)
    ElMessage.success('发布成功')
    await loadFaqList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '发布失败')
  }
}

const handleArchive = async (item) => {
  openMoreId.value = null
  try {
    await archiveFaqItem(item.id)
    ElMessage.success('归档成功')
    await loadFaqList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '归档失败')
  }
}

const handleToggleTop = async (item) => {
  openMoreId.value = null
  try {
    await toggleFaqItemTop(item.id)
    ElMessage.success(item.isTop ? '已取消置顶' : '已置顶')
    await loadFaqList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// ==================== 更多菜单 ====================
const openMoreId = ref(null)
const openMoreItem = ref(null)
const moreMenuPos = reactive({ top: 0, left: 0 })
const moreBtnRefs = {}

const moreMenuStyle = computed(() => ({
  top: `${moreMenuPos.top}px`,
  left: `${moreMenuPos.left}px`
}))

const setMoreBtnRef = (id, el) => {
  if (el) moreBtnRefs[id] = el
}

const toggleMoreMenu = (id, event) => {
  if (openMoreId.value === id) {
    openMoreId.value = null
    openMoreItem.value = null
  } else {
    const item = faqList.value.find(f => f.id === id)
    openMoreItem.value = item
    openMoreId.value = id
    // 计算菜单位置
    const btn = event.currentTarget
    const rect = btn.getBoundingClientRect()
    moreMenuPos.top = rect.bottom + 4
    moreMenuPos.left = rect.right - 144 // 144 = w-36 (9rem)
  }
}

const handleClickOutside = (e) => {
  if (openMoreId.value && !e.target.closest('.more-menu-container')) {
    openMoreId.value = null
    openMoreItem.value = null
  }
}

// ==================== 版本历史 ====================
const showVersionDrawer = ref(false)
const versionList = ref([])
const versionLoading = ref(false)
const versionFaqItem = ref(null)

const openVersionDrawer = async (item) => {
  versionFaqItem.value = item
  showVersionDrawer.value = true
  versionLoading.value = true
  try {
    versionList.value = await getFaqVersions(item.id)
  } catch (error) {
    ElMessage.error(error.message || '加载版本失败')
  } finally {
    versionLoading.value = false
  }
}

const handleRollback = async (version) => {
  try {
    await rollbackFaqItem(versionFaqItem.value.id, version.versionNumber)
    ElMessage.success('回滚成功')
    showVersionDrawer.value = false
    await loadFaqList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '回滚失败')
  }
}

// ==================== 删除确认 ====================
const showConfirmModal = ref(false)
const confirmMessage = ref('')
const confirmPayload = ref({ type: '', id: null })

const confirmDeleteCategory = (node) => {
  confirmPayload.value = { type: 'category', id: node.id }
  confirmMessage.value = `确认删除分类「${node.name}」？分类下的子分类和FAQ将无法访问。`
  showConfirmModal.value = true
}

const confirmDeleteFaq = (item) => {
  openMoreId.value = null
  confirmPayload.value = { type: 'faq', id: item.id }
  confirmMessage.value = `确认删除FAQ「${item.title}」？`
  showConfirmModal.value = true
}

const confirmDeleteAction = async () => {
  const { type, id } = confirmPayload.value
  try {
    if (type === 'category') {
      await deleteFaqCategory(id)
      ElMessage.success('分类已删除')
      await loadCategories()
    } else {
      await deleteFaqItem(id)
      ElMessage.success('FAQ已删除')
      await loadFaqList(pageInfo.current)
    }
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  } finally {
    showConfirmModal.value = false
  }
}

// ==================== 初始化 ====================
onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  await loadCategories()
  await loadFaqList(1)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* 分类弹窗过渡动画 */
.modal-enter-active {
  transition: opacity 0.25s ease-out;
}
.modal-leave-active {
  transition: opacity 0.2s ease-in;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white {
  transform: translateY(0);
  opacity: 1;
}
.modal-enter-from .bg-white {
  transform: translateY(16px);
  opacity: 0;
}
.modal-leave-to .bg-white {
  transform: translateY(8px);
  opacity: 0;
}
.modal-enter-active .bg-white,
.modal-leave-to .bg-white {
  transition: transform 0.3s ease-out, opacity 0.25s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .modal-enter-active,
  .modal-leave-active {
    transition: none;
  }
  .modal-enter-active .bg-white,
  .modal-leave-to .bg-white {
    transform: none;
    transition: none;
  }
}
</style>
